import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { Check, Info, Loader2, UserCog } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { LIFT, LINK } from './VaGovStep';
import { Picker } from './ClientOnboardingSlideOver';

// Claims Manager preferences (Nic 2026-11-02): the last checklist step,
// asked on a call just before Ready for CM. Three things, every one a
// checklist answer (PUT /:id/answers):
//   cm_pref_gender     a woman, a man, or no preference (the step is done
//                      once this is answered)
//   cm_pref_requested  the Claims Manager they asked for, if any
//   cm_pref_note       anything sensitive that should shape the pick (optional;
//                      for whoever assigns, never sent to the CM)
// All of it shows on CM Assignment, informational only. 'legacy' is the
// grandfather answer (migration 235) for clients already at Ready for CM
// when the step arrived: the step reads done but says "not asked".

const GENDER_OPTIONS = [
  { key: 'woman', label: 'A woman' },
  { key: 'man', label: 'A man' },
  { key: 'none', label: 'No preference' },
];
const NOTE_MAX = 250;

const Choice = ({ options, value, disabled, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = value === o.key;
      return (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          onClick={() => !on && onChange(o.key)}
          aria-pressed={on}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-2 px-3.5 text-[13px] outline-none transition-[transform,box-shadow] duration-150 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60',
            on
              ? 'scale-[1.03] border-violet-500 bg-violet-500/10 font-semibold text-violet-700 shadow-[0_0_12px_rgba(139,92,246,0.15)] dark:text-violet-300'
              : cn(
                  'border-slate-200 bg-white font-normal text-slate-400 dark:border-white/10 dark:!bg-white/5 dark:text-slate-400',
                  !disabled && 'hover:border-violet-500',
                ),
          )}
        >
          {on ? <Check size={13} /> : null}
          {o.label}
        </button>
      );
    })}
  </div>
);

const CmPreferencesStep = ({
  step,
  firstName,
  answers = {},
  answerMeta = {},
  claimsManagers = [],
  canAct,
  saving = false,
  onAnswer,
}) => {
  const who = firstName || 'them';
  const gender = answers.cm_pref_gender || null;
  const legacy = gender === 'legacy';
  const asked = GENDER_OPTIONS.some((o) => o.key === gender);
  const requested = answers.cm_pref_requested || null;
  const savedNote = answers.cm_pref_note || '';
  // The note stages locally and saves on Save (one answer, not one per key).
  const [note, setNote] = useState(savedNote);
  useEffect(() => setNote(savedNote), [savedNote]);
  const noteDirty = note.trim() !== savedNote.trim();
  const options =
    step?.fields?.find((f) => f.id === 'cm_pref_gender')?.options ||
    GENDER_OPTIONS;
  const meta = answerMeta.cm_pref_gender || null;
  // Never dimmed while a save is in flight (Nic 2026-11-03: every control
  // blinked on each pick). The answer shows at once from the Workspace's
  // pending merge; only Save note and Undo wait on the round trip.
  const disabled = !canAct;
  // The requested CM may have left the rotation since: keep their id as a
  // choice so the picker still shows a name.
  const cmOptions = [
    { value: '', label: 'Nobody in particular' },
    ...claimsManagers.map((c) => ({ value: c.userId, label: c.name })),
    ...(requested && !claimsManagers.some((c) => c.userId === requested)
      ? [
          {
            value: requested,
            label: 'A Claims Manager no longer on the rotation',
          },
        ]
      : []),
  ];

  return (
    <div className="mt-1.5">
      {step?.need ? (
        <p className="mb-0 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
          {step.need}
        </p>
      ) : null}

      {legacy ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:!bg-amber-500/10 dark:text-amber-200">
          <Info size={13} className="mt-0.5 shrink-0 opacity-70" />
          <span>
            Not asked: {who} reached Ready for CM before this step existed, so
            it counts as done. Ask on their next call and the answers replace
            this.
          </span>
        </div>
      ) : null}

      {/* The questions stay up once answered (Nic 2026-11-03): the CM
          pick and the note come after the first one. */}
      {step?.ask?.length ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50/70 px-3 py-2 text-xs leading-relaxed text-indigo-900 dark:!bg-indigo-500/10 dark:text-indigo-200">
          <Info size={13} className="mt-0.5 shrink-0 opacity-70" />
          <div className="min-w-0">
            <div className="font-semibold">Ask them:</div>
            <ul className="mb-0 mt-1 list-disc space-y-0.5 pl-4">
              {step.ask.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className={cn('mt-[16px] rounded-2xl p-[18px]', LIFT)}>
        <p className="mb-2 text-[13px] font-medium text-slate-600 dark:text-slate-300">
          Would rather work with
        </p>
        <Choice
          options={options}
          value={asked ? gender : null}
          disabled={disabled}
          onChange={(v) => onAnswer('cm_pref_gender', v)}
        />
        {asked && meta?.name ? (
          <div className="mt-1.5 flex items-center gap-2 text-[11px] italic text-slate-400 dark:text-slate-500">
            Asked by {meta.name}
            {meta.at ? ` · ${moment(meta.at).fromNow()}` : ''}
            {canAct ? (
              <button
                type="button"
                onClick={() => onAnswer('cm_pref_gender', null)}
                className={cn(LINK, 'not-italic')}
              >
                Undo
              </button>
            ) : null}
          </div>
        ) : null}

        <p className="mb-2 mt-[16px] text-[13px] font-medium text-slate-600 dark:text-slate-300">
          <UserCog
            size={13}
            className="mr-1 inline-block align-[-2px] opacity-60"
          />
          Asked for a Claims Manager
        </p>
        <div className="max-w-xs">
          <Picker
            value={requested || ''}
            placeholder="Nobody in particular"
            options={cmOptions}
            disabled={disabled}
            title="The Claims Manager they asked for, if they named one"
            onPick={(v) => onAnswer('cm_pref_requested', v || null)}
          />
        </div>

        <p className="mb-2 mt-[16px] text-[13px] font-medium text-slate-600 dark:text-slate-300">
          Sensitive topics{' '}
          <span className="font-normal text-slate-400">(optional)</span>
        </p>
        <textarea
          value={note}
          disabled={disabled}
          maxLength={NOTE_MAX}
          rows={3}
          placeholder="Anything that should shape who they get, in their words. For whoever assigns the Claims Manager."
          onChange={(e) => setNote(e.target.value)}
          className="block w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] leading-relaxed text-slate-800 outline-none placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/30 disabled:opacity-60 dark:border-white/10 dark:!bg-white/5 dark:text-slate-100"
        />
        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-400">
          <span className="tabular-nums">
            {note.length}/{NOTE_MAX}
          </span>
          {noteDirty ? (
            <button
              type="button"
              onClick={() => onAnswer('cm_pref_note', note.trim() || null)}
              className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-violet-700 disabled:opacity-60 dark:!text-white"
            >
              {saving ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Check size={11} />
              )}
              Save note
            </button>
          ) : savedNote && answerMeta.cm_pref_note?.name ? (
            <span className="italic">
              Noted by {answerMeta.cm_pref_note.name}
              {answerMeta.cm_pref_note.at
                ? ` · ${moment(answerMeta.cm_pref_note.at).fromNow()}`
                : ''}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CmPreferencesStep;
